import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionExpiryDialog from '../components/common/SessionExpiryDialog';
import { post } from '../utils/api';
import { useAuth } from './AuthContext';

// Global session timeout for authenticated users. Idle timeout from env, then 60s warning before logout.
const AdminSessionTimeoutContext = createContext(null);

// Match backend SESSION_IDLE_TIMEOUT_MS; default 30 min.
const IDLE_MS = parseInt(process.env.REACT_APP_ADMIN_IDLE_TIMEOUT_MS, 10) || 30 * 60 * 1000;
const WARNING_MS = 60 * 1000;
// Throttle backend touch so scroll/mousemove does not spam the server.
const BACKEND_TOUCH_THROTTLE_MS = 45 * 1000;
const LOCAL_ACTIVITY_THROTTLE_MS = 3 * 1000;

export const AdminSessionTimeoutProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const idleTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastBackendTouchRef = useRef(0);
  const lastLocalActivityRef = useRef(0);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    idleTimerRef.current = null;
    expiryTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const endSession = useCallback(async () => {
    clearTimers();
    setWarningOpen(false);
    try {
      await logout();
    } catch {}
    navigate('/login', { replace: true });
  }, [clearTimers, logout, navigate]);

  const startCountdown = useCallback(() => {
    let remaining = 60;
    setSecondsRemaining(remaining);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    setWarningOpen(false);
    idleTimerRef.current = setTimeout(() => {
      setWarningOpen(true);
      startCountdown();
    }, IDLE_MS);
    expiryTimerRef.current = setTimeout(() => {
      void endSession();
    }, IDLE_MS + WARNING_MS);
  }, [clearTimers, endSession, startCountdown]);

  const touchBackendSession = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      await post('/api/session/touch', {});
    } catch {
      // 401 handled elsewhere; do not logout here (api.js avoids auto-logout on 401)
    }
  }, [isAuthenticated]);

  const touchBackendThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastBackendTouchRef.current < BACKEND_TOUCH_THROTTLE_MS) return;
    lastBackendTouchRef.current = now;
    void touchBackendSession();
  }, [touchBackendSession]);

  const resetIdleClock = useCallback(
    (options = {}) => {
      if (!isAuthenticated) return;
      const { touchBackend = true, immediateBackend = false } = options;
      setWarningOpen((prev) => {
        if (!prev) startTimers();
        return prev;
      });
      if (!touchBackend) return;
      if (immediateBackend) {
        lastBackendTouchRef.current = Date.now();
        void touchBackendSession();
      } else {
        touchBackendThrottled();
      }
    },
    [isAuthenticated, startTimers, touchBackendSession, touchBackendThrottled]
  );

  const extend = useCallback(() => {
    startTimers();
    setWarningOpen(false);
    lastBackendTouchRef.current = Date.now();
    void touchBackendSession();
  }, [startTimers, touchBackendSession]);

  // Any page navigation resets the 30-minute idle clock (frontend + backend).
  useEffect(() => {
    if (!isAuthenticated) return;
    resetIdleClock({ immediateBackend: true });
  }, [location.pathname, isAuthenticated, resetIdleClock]);

  // UI activity: click, keys, scroll, mouse — extends idle window.
  useEffect(() => {
    const onActivity = () => {
      if (!isAuthenticated) return;
      const now = Date.now();
      if (now - lastLocalActivityRef.current < LOCAL_ACTIVITY_THROTTLE_MS) return;
      lastLocalActivityRef.current = now;
      resetIdleClock();
    };
    document.addEventListener('click', onActivity, { passive: true });
    document.addEventListener('touchstart', onActivity, { passive: true });
    document.addEventListener('keydown', onActivity, { passive: true });
    document.addEventListener('scroll', onActivity, { passive: true });
    document.addEventListener('mousemove', onActivity, { passive: true });
    return () => {
      document.removeEventListener('click', onActivity);
      document.removeEventListener('touchstart', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('scroll', onActivity);
      document.removeEventListener('mousemove', onActivity);
    };
  }, [isAuthenticated, resetIdleClock]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setWarningOpen(false);
      return;
    }
    startTimers();
  }, [isAuthenticated, startTimers, clearTimers]);

  const value = useMemo(
    () => ({
      noteActivity: resetIdleClock,
      extend,
      endSession,
      warningOpen,
      secondsRemaining,
    }),
    [resetIdleClock, extend, endSession, warningOpen, secondsRemaining]
  );

  return (
    <AdminSessionTimeoutContext.Provider value={value}>
      {children}
      {isAuthenticated && (
        <SessionExpiryDialog
          open={warningOpen}
          secondsRemaining={secondsRemaining}
          onExtend={extend}
          onEnd={endSession}
        />
      )}
    </AdminSessionTimeoutContext.Provider>
  );
};

export const useAdminSessionTimeout = () => useContext(AdminSessionTimeoutContext);
